import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Star, MessageSquare, Armchair, Bell, Volume2 } from 'lucide-react';
import type { Session } from '@/types';
import { useSocket } from '@/hooks/useSocket';
import { useWebPush } from '@/hooks/useWebPush';
import { QrDisplay } from '@/components/QrDisplay';
import { Toaster, toast } from 'sonner';

const API_BASE =
  window.location.hostname === 'localhost'
    ? 'http://localhost:8081/api/v1'
    : '/api/v1';

export function ClientPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notified, setNotified] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const { connected, lastEvent, joinSession } = useSocket();
  const { subscribed, subscribe, supported } = useWebPush();

  const fetchSession = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/sessions/${sessionId}`);
      if (!res.ok) throw new Error('Sesión no encontrada');
      const data = (await res.json()) as Session;
      setSession(data);
      if (data.status === 'notified') setNotified(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  useEffect(() => {
    if (sessionId && connected) {
      joinSession(sessionId);
    }
  }, [sessionId, connected, joinSession]);

  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.event === 'session:notified' || lastEvent.event === 'session:updated') {
      const data = lastEvent.data as { status?: string };
      if (data?.status === 'notified') {
        setNotified(true);
        fetchSession();
        // Vibrate and sound
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        try {
          const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = 880;
          gain.gain.setValueAtTime(0.1, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
          osc.start();
          osc.stop(ctx.currentTime + 0.4);
        } catch {
          // ignore
        }
      }
      if (data?.status === 'seated' || data?.status === 'served') {
        // Show feedback after 30 seconds
        const t = setTimeout(() => setShowFeedback(true), 30000);
        fetchSession();
        return () => clearTimeout(t);
      }
    }
  }, [lastEvent, fetchSession]);

  const handleCancel = async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API_BASE}/sessions/${sessionId}/cancel`, { method: 'POST' });
      if (res.ok) {
        fetchSession();
        toast.info('Turno cancelado');
      }
    } catch {
      // ignore
    }
  };

  const handleSeat = async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API_BASE}/sessions/${sessionId}/seat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const t = setTimeout(() => setShowFeedback(true), 30000);
        fetchSession();
        return () => clearTimeout(t);
      }
    } catch {
      // ignore
    }
  };

  const handleFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionId || rating < 1) return;
    try {
      const res = await fetch(`${API_BASE}/sessions/${sessionId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment: comment || undefined }),
      });
      if (res.ok) {
        setFeedbackSent(true);
        toast.success('¡Gracias por tu valoración!');
      }
    } catch {
      toast.error('Error al enviar valoración');
    }
  };

  const handlePushSubscribe = async () => {
    if (!sessionId) return;
    try {
      await subscribe(sessionId);
      toast.success('Notificaciones activadas');
    } catch {
      toast.error('No se pudieron activar las notificaciones');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--qr-bg)' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-xl animate-skeleton" style={{ background: 'var(--qr-surface)' }} />
          <div className="w-32 h-4 rounded animate-skeleton" style={{ background: 'var(--qr-surface)' }} />
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="flex items-center justify-center min-h-screen px-6" style={{ background: 'var(--qr-bg)' }}>
        <div className="text-center">
          <p className="text-xl font-bold" style={{ color: 'var(--qr-danger)' }}>{error || 'Sesión no encontrada'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-center min-h-screen py-8 px-4 animate-fade-in" style={{ background: 'var(--qr-bg)' }}>
      <Toaster position="bottom-center" richColors />
      {notified && <ConfettiOverlay />}

      <QrDisplay session={session} business={null} onCancel={handleCancel} />

      {supported && !subscribed && session.status === 'waiting' && (
        <button
          onClick={handlePushSubscribe}
          className="flex items-center justify-center gap-2 w-full max-w-md mt-4 py-3 px-4 rounded-xl font-semibold transition-transform duration-200 active:scale-[0.98] animate-bell"
          style={{ background: 'var(--qr-primary)', color: '#fff' }}
        >
          <Bell size={18} />
          Permitir notificaciones
        </button>
      )}

      {subscribed && (
        <div className="flex items-center gap-2 mt-3 text-sm" style={{ color: 'var(--qr-success)' }}>
          <Volume2 size={16} />
          Notificaciones activas
        </div>
      )}

      {notified && session.status === 'notified' && (
        <div className="flex flex-col items-center gap-3 mt-4 animate-fade-in">
          <p className="text-xl font-bold text-center" style={{ color: 'var(--qr-success)' }}>
            ¡Tu mesa está lista!
          </p>
          {session.table_number && (
            <p className="text-lg" style={{ color: 'var(--qr-text)' }}>
              Dirígete a la mesa {session.table_number}
            </p>
          )}
          <button
            onClick={handleSeat}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-transform duration-200 active:scale-[0.98]"
            style={{ background: 'var(--qr-success)', color: '#fff' }}
          >
            <Armchair size={18} />
            He llegado a mi mesa
          </button>
        </div>
      )}

      {(showFeedback || session.status === 'served' || session.status === 'seated') && !feedbackSent && (
        <div
          className="w-full max-w-sm mt-8 p-5 rounded-2xl animate-slide-up"
          style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
        >
          <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
            <MessageSquare size={18} />
            ¿Cómo fue tu experiencia?
          </h3>
          <form onSubmit={handleFeedback} className="flex flex-col gap-3">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setRating(s)}
                  className="transition-transform duration-150 active:scale-90"
                >
                  <Star
                    size={32}
                    fill={s <= rating ? 'var(--qr-warning)' : 'transparent'}
                    color={s <= rating ? 'var(--qr-warning)' : 'var(--qr-border)'}
                  />
                </button>
              ))}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Comentario (opcional)"
              rows={3}
              maxLength={200}
              className="w-full px-3 py-2 rounded-lg outline-none resize-none"
              style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
            />
            <div className="text-right text-xs" style={{ color: 'var(--qr-text-secondary)' }}>
              {comment.length}/200
            </div>
            <button
              type="submit"
              disabled={rating < 1}
              className="w-full py-2.5 rounded-xl font-semibold transition-transform duration-200 active:scale-[0.98] disabled:opacity-50"
              style={{ background: 'var(--qr-primary)', color: '#fff' }}
            >
              Enviar valoración
            </button>
          </form>
        </div>
      )}

      {feedbackSent && (
        <div className="mt-6 text-center animate-fade-in">
          <p className="text-lg font-semibold" style={{ color: 'var(--qr-success)' }}>
            ¡Gracias por tu valoración!
          </p>
          <div className="mt-2 flex justify-center gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full animate-fade-in"
                style={{
                  background: ['var(--qr-primary)', 'var(--qr-success)', 'var(--qr-warning)', 'var(--qr-accent)', 'var(--qr-danger)'][i],
                  animationDelay: `${i * 100}ms`,
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ConfettiOverlay() {
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {Array.from({ length: 40 }).map((_, i) => (
        <div
          key={i}
          className="absolute w-2 h-2 rounded-sm animate-fade-in"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            background: ['var(--qr-primary)', 'var(--qr-success)', 'var(--qr-warning)', 'var(--qr-accent)'][i % 4],
            animationDelay: `${Math.random() * 0.5}s`,
            animationDuration: `${1 + Math.random()}s`,
            opacity: 0.8,
            transform: `rotate(${Math.random() * 360}deg)`,
          }}
        />
      ))}
    </div>
  );
}
